import { JSX, useState } from 'react';
import { generatePath } from 'react-router';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { Button } from 'Components/Buttons/Buttons';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { TextToast } from 'Components/Toast/Toast';
import { ICON_SUBMIT } from 'Config/Icons';
import { SETTINGS, TPRM_TIMEFRAMES_CONFIGURATION, UCRM_TIMEFRAMES_CONFIGURATION } from 'Config/PathTemplates';
import { RequestState } from 'Helpers/RequestUtils';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceTimeframes, ReassessmentIntervals, UpdateDueDiligenceTimeframesRequest } from 'Models/DueDiligenceWorkflow';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import styles from './TimeframesConfiguration.module.css';

export interface TimeframesConfigurationLoaderProps {
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
}

export const TimeframesConfigurationLoader = (props: TimeframesConfigurationLoaderProps): JSX.Element => {
    return <PageDataLoader path={props.module === ProductModule.UCRM ? UCRM_TIMEFRAMES_CONFIGURATION : TPRM_TIMEFRAMES_CONFIGURATION} fetchData={() => Promise.all([props.dueDiligenceAssessmentConfigurationApi.getTimeframes(), props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()])} renderForm={(result) => <TimeframesConfiguration initialTimeframes={result[0].data} assessmentConfigurations={result[1].data} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} module={props.module} />} />;
};

interface TimeframeRow {
    assessmentId: string;
    assessmentName: string;
    internalDays?: number;
    thirdPartyDays?: number;
}

interface TimeframesConfigurationProps {
    assessmentConfigurations: DueDiligenceAssessmentConfiguration[];
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    initialTimeframes: DueDiligenceTimeframes;
    module: DueDiligenceModule;
}

/**
 * Allows Admin users to configure timing-related settings for TPRM or UCRM, depending on `module`:
 * * How often third-party services (TPRM) or use cases (UCRM) should be reassessed, based on inherent risk rating.
 * * How many days it is expected to complete the IRQ, due diligence questionnaires and assessments, and Final Review.
 *
 * NOTE: The user can type anything into the number inputs, including non-numbers, negative numbers, and zero. Getting this right on the frontend is actually a bit tricky, so we just lean on the backend for validation here. It would be ideal to add a reusable component to the app at some point, perhaps if/when we ever overhaul forms in a way that includes per-field validation error messages.
 */
const TimeframesConfiguration = (props: TimeframesConfigurationProps): JSX.Element => {
    const isUcrm = props.module === ProductModule.UCRM;
    const assessedEntities = isUcrm ? 'use cases' : 'third-party services';
    const [expectedDaysToCompleteIrq, setExpectedDaysToCompleteIrq] = useState<number | undefined>(props.initialTimeframes.expected_days_to_complete_irq);
    const [assessmentTimeframes, setAssessmentTimeframes] = useState<TimeframeRow[]>(
        props.assessmentConfigurations.map((config) => ({
            assessmentId: config.id,
            assessmentName: config.name,
            thirdPartyDays: config.expected_days_to_complete_questionnaire,
            internalDays: config.expected_days_to_complete_assessment,
        }))
    );
    const [expectedDaysToCompleteFinalReview, setExpectedDaysToCompleteFinalReview] = useState<number | undefined>(props.initialTimeframes.expected_days_to_complete_final_review);
    const [reassessmentIntervals, setReassessmentIntervals] = useState<ReassessmentIntervals>(props.initialTimeframes.service_reassessment_intervals);
    const [requestState, setRequestState] = useState<RequestState>({ type: 'idle' });

    const handleSubmit = async (): Promise<void> => {
        setRequestState({ type: 'inProgress' });
        try {
            const request: UpdateDueDiligenceTimeframesRequest = {
                timeframes: {
                    expected_days_to_complete_irq: expectedDaysToCompleteIrq,
                    expected_days_to_complete_final_review: expectedDaysToCompleteFinalReview,
                    service_reassessment_intervals: reassessmentIntervals,
                },
                expected_durations_for_assessments: assessmentTimeframes.map((timeframes) => ({
                    assessment_id: timeframes.assessmentId,
                    expected_days_to_complete_questionnaire: timeframes.thirdPartyDays,
                    expected_days_to_complete_assessment: timeframes.internalDays,
                })),
            };
            await props.dueDiligenceAssessmentConfigurationApi.updateTimeframes(request);
            setRequestState({ type: 'success', message: 'Timeframes saved.' });
        } catch (error) {
            console.error(error.message);
            setRequestState({ type: 'failure', message: error.message });
        }
    };

    const handleIrqChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        setExpectedDaysToCompleteIrq(value === '' ? undefined : parseInt(value, 10));
    };

    const handleFinalReviewChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        setExpectedDaysToCompleteFinalReview(value === '' ? undefined : parseInt(value, 10));
    };

    const updateAssessmentTimeframe = (index: number, field: 'thirdPartyDays' | 'internalDays', value: number | undefined): void => {
        setAssessmentTimeframes((prev) => prev.map((at, i) => (i === index ? { ...at, [field]: value } : at)));
    };

    const handleAssessmentThirdPartyDaysChange = (index: number, event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        updateAssessmentTimeframe(index, 'thirdPartyDays', value === '' ? undefined : parseInt(value, 10));
    };

    const handleAssessmentInternalDaysChange = (index: number, event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        updateAssessmentTimeframe(index, 'internalDays', value === '' ? undefined : parseInt(value, 10));
    };

    const handleReassessmentIntervalChange = (riskRating: keyof ReassessmentIntervals, event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        setReassessmentIntervals((prev) => ({
            ...prev,
            [riskRating]: value === '' ? undefined : parseInt(value, 10),
        }));
    };

    return (
        <>
            {requestState?.type === 'success' && <TextToast variant="success" clearToast={() => setRequestState({ type: 'idle' })} text={requestState.message} />}
            {requestState?.type === 'failure' && <TextToast variant="failure" clearToast={() => setRequestState({ type: 'idle' })} text={requestState.message} />}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                        <BreadcrumbText>Timeframes</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Timeframes"
                headerDescription={`Configure the expected durations of tasks assigned to ${isUcrm ? 'users' : 'internal users and third-party users'}. Durations configured here are used to suggest due dates for due diligence workflow tasks.`}
                headerButtons={
                    <Button variant="primary" onClick={handleSubmit} isLoading={requestState?.type === 'inProgress'} loadingText="Saving..." fontAwesomeImage={ICON_SUBMIT}>
                        Save
                    </Button>
                }
                body={[
                    {
                        title: 'Due Diligence Workflow Schedule',
                        content: (
                            <>
                                <Text noStyles>{`Configure how often ${assessedEntities} should be reassessed based on their inherent risk rating. Leaving a field blank indicates that corresponding ${assessedEntities} should not be reassessed.`}</Text>
                                <div className={styles.fieldContainer}>
                                    <FormFieldText formFieldId="reassessment-low" formFieldLabel="Low risk (months)" formFieldType="number" value={reassessmentIntervals.low ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleReassessmentIntervalChange('low', event)} min={1} />
                                    <FormFieldText formFieldId="reassessment-low-moderate" formFieldLabel="Low/Moderate risk (months)" formFieldType="number" value={reassessmentIntervals.low_moderate ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleReassessmentIntervalChange('low_moderate', event)} min={1} />
                                    <FormFieldText formFieldId="reassessment-moderate" formFieldLabel="Moderate risk (months)" formFieldType="number" value={reassessmentIntervals.moderate ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleReassessmentIntervalChange('moderate', event)} min={1} />
                                    <FormFieldText formFieldId="reassessment-moderate-high" formFieldLabel="Moderate/High risk (months)" formFieldType="number" value={reassessmentIntervals.moderate_high ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleReassessmentIntervalChange('moderate_high', event)} min={1} />
                                    <FormFieldText formFieldId="reassessment-high" formFieldLabel="High risk (months)" formFieldType="number" value={reassessmentIntervals.high ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleReassessmentIntervalChange('high', event)} min={1} />
                                </div>
                            </>
                        ),
                    },
                    {
                        title: 'Due Diligence Workflow',
                        content: (
                            <>
                                <Text variant="Header3">Inherent risk questionnaire</Text>
                                <div className={styles.section}>
                                    <div className={styles.fieldContainer}>
                                        <FormFieldText formFieldId="irq-internal-days" formFieldLabel="Days to complete" formFieldType="number" value={expectedDaysToCompleteIrq ?? ''} handleChange={handleIrqChange} min={1} />
                                    </div>
                                </div>

                                <Text variant="Header3">Assessments</Text>
                                <Text variant="Text4">It is assumed that assessments can be completed in parallel.</Text>
                                {assessmentTimeframes.map((at, index) => (
                                    <div key={at.assessmentId} className={styles.section}>
                                        <Text variant="Header4">{at.assessmentName}</Text>
                                        <div className={styles.fieldContainer}>
                                            <FormFieldText formFieldId={`assessment-${at.assessmentId}-third-party-days`} formFieldLabel={`Days for ${isUcrm ? 'users' : 'third-party users'} to complete questionnaire`} formFieldType="number" value={at.thirdPartyDays ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAssessmentThirdPartyDaysChange(index, event)} min={1} />
                                            <FormFieldText formFieldId={`assessment-${at.assessmentId}-internal-days`} formFieldLabel={`Days for ${isUcrm ? 'users' : 'internal users'} to complete assessment`} formFieldType="number" value={at.internalDays ?? ''} handleChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAssessmentInternalDaysChange(index, event)} min={1} />
                                        </div>
                                    </div>
                                ))}

                                <Text variant="Header3">Final Review</Text>
                                <div className={styles.section}>
                                    <div className={styles.fieldContainer}>
                                        <FormFieldText formFieldId="final-review-internal-days" formFieldLabel="Days to complete" formFieldType="number" value={expectedDaysToCompleteFinalReview ?? ''} handleChange={handleFinalReviewChange} min={1} />
                                    </div>
                                </div>
                            </>
                        ),
                    },
                ]}
            />
        </>
    );
};
